import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, AuthUser } from '../services/authApi';
import api from '../services/api';
import ErrorDisplay from '../components/ui/ErrorDisplay';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;

  /** Показать модалку авторизации. Возвращает Promise<AuthUser> если успех, null если закрыли. */
  requestAuth: () => Promise<AuthUser | null>;

  /** Выйти из аккаунта */
  logout: () => void;

  /** Обновить профиль */
  updateProfile: (data: { name?: string }) => Promise<AuthUser>;

  /** Обновить флаг preferencesConfigured локально (после сохранения) */
  updatePreferencesConfigured: (configured: boolean) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'swipeeat_auth_token';
const USER_KEY = 'swipeeat_auth_user';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(false);

  // Для управления модалкой авторизации
  const [showAuthModal, setShowAuthModal] = useState(false);
  const authResolveRef = useRef<((user: AuthUser | null) => void) | null>(null);

  // Устанавливаем Authorization хедер при наличии токена
  useEffect(() => {
    const interceptorId = api.interceptors.request.use((config) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) {
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });

    return () => {
      api.interceptors.request.eject(interceptorId);
    };
  }, []);

  // При наличии токена — проверяем его валидность
  useEffect(() => {
    if (token && !user) {
      setLoading(true);
      authApi.getMe()
        .then(({ user: u }) => {
          setUser(u);
          localStorage.setItem(USER_KEY, JSON.stringify(u));
        })
        .catch(() => {
          // Токен невалидный — чистим
          setToken(null);
          setUser(null);
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        })
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback((newToken: string, newUser: AuthUser) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  const requestAuth = useCallback((): Promise<AuthUser | null> => {
    // Если уже авторизован — сразу возвращаем
    if (user && token) {
      return Promise.resolve(user);
    }

    // Показываем модалку и ждём результат
    return new Promise((resolve) => {
      authResolveRef.current = resolve;
      setShowAuthModal(true);
    });
  }, [user, token]);

  const handleAuthSuccess = useCallback((newToken: string, newUser: AuthUser) => {
    login(newToken, newUser);
    setShowAuthModal(false);
    authResolveRef.current?.(newUser);
    authResolveRef.current = null;
    if (!newUser.preferencesConfigured) {
      navigate('/preferences/setup');
    }
  }, [login, navigate]);

  const handleAuthClose = useCallback(() => {
    setShowAuthModal(false);
    authResolveRef.current?.(null);
    authResolveRef.current = null;
  }, []);

  const updateProfile = useCallback(async (data: { name?: string }) => {
    const { user: updated } = await authApi.updateProfile(data);
    setUser(updated);
    localStorage.setItem(USER_KEY, JSON.stringify(updated));
    return updated;
  }, []);

  const updatePreferencesConfigured = useCallback((configured: boolean) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, preferencesConfigured: configured };
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isAuthenticated: !!user && !!token,
        requestAuth,
        logout,
        updateProfile,
        updatePreferencesConfigured,
      }}
    >
      {children}
      {showAuthModal && (
        <AuthModal onSuccess={handleAuthSuccess} onClose={handleAuthClose} />
      )}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// ========================================
// PrivacyAgreementSheet — полный текст соглашения
// ========================================

const PRIVACY_TEXT = `СОГЛАСИЕ НА ОБРАБОТКУ ПЕРСОНАЛЬНЫХ ДАННЫХ

Настоящим я, пользователь сервиса SwipeEat (далее — «Сервис»), расположенного по адресу swipeeat.ru, в соответствии с требованиями Федерального закона от 27.07.2006 № 152-ФЗ «О персональных данных», даю своё согласие ИП / оператору Сервиса (далее — «Оператор») на обработку следующих персональных данных:

— адрес электронной почты (email);
— имя пользователя (при его указании);
— история сессий и предпочтения (кухни, блюда).

ЦЕЛИ ОБРАБОТКИ
Персональные данные обрабатываются в целях:
— идентификации пользователя и предоставления доступа к функциям Сервиса;
— сохранения предпочтений и истории сессий;
— направления технических уведомлений (коды подтверждения).

ПЕРЕЧЕНЬ ДЕЙСТВИЙ
Оператор вправе совершать следующие действия с персональными данными: сбор, запись, систематизация, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, удаление, уничтожение.

СРОК ДЕЙСТВИЯ
Настоящее согласие действует до момента его отзыва. Отзыв осуществляется путём направления письменного запроса через форму обратной связи на сайте или по электронной почте поддержки.

ХРАНЕНИЕ ДАННЫХ
Персональные данные хранятся на серверах, расположенных в Российской Федерации, и не передаются третьим лицам, за исключением случаев, предусмотренных законодательством РФ.

Нажимая кнопку «Получить код» и подтверждая согласие, пользователь подтверждает, что ознакомлен с настоящим документом и даёт согласие на обработку своих персональных данных на указанных условиях.`;

const PrivacySheet: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="fixed inset-0 z-[60] flex items-end justify-center">
    <div className="absolute inset-0 bg-black/50" onClick={onClose} />
    <div className="relative bg-white w-full rounded-t-2xl shadow-xl flex flex-col max-h-[85vh] sm:max-w-lg sm:rounded-2xl sm:mb-8 sm:max-h-[80vh]">
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
        <h3 className="text-base font-bold text-gray-900">Обработка персональных данных</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className="overflow-y-auto px-5 py-4 flex-1">
        <pre className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap font-sans">{PRIVACY_TEXT}</pre>
      </div>
      <div className="px-5 pb-6 pt-3 flex-shrink-0">
        <button
          onClick={onClose}
          className="w-full py-3 rounded-xl bg-orange-500 text-white font-semibold text-sm hover:bg-orange-600 transition-all"
        >
          Понятно
        </button>
      </div>
    </div>
  </div>
);

// ========================================
// AuthModal — встроен в тот же файл для простоты
// ========================================

interface AuthModalProps {
  onSuccess: (token: string, user: AuthUser) => void;
  onClose: () => void;
}

type Step = 'contact' | 'otp';

const RESEND_COOLDOWN = 60;

const AuthModal: React.FC<AuthModalProps> = ({ onSuccess, onClose }) => {
  const [step, setStep] = useState<Step>('contact');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Privacy consent
  const [needsConsent, setNeedsConsent] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacySheet, setShowPrivacySheet] = useState(false);

  // Resend OTP cooldown
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startResendTimer = () => {
    setResendCooldown(RESEND_COOLDOWN);
    resendTimerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  const doRequestOtp = async (withConsent: boolean) => {
    setError('');
    setLoading(true);
    try {
      const result = await authApi.requestOtp({
        email,
        ...(withConsent ? { privacyAccepted: true } : {}),
      });
      if (result.needsConsent) {
        setNeedsConsent(true);
      } else {
        setNeedsConsent(false);
        setStep('otp');
        startResendTimer();
      }
    } catch (err: any) {
      setError(err.message || 'Не удалось отправить код');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Введите корректный email-адрес');
      return;
    }
    await doRequestOtp(privacyAccepted);
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      await authApi.requestOtp({ email });
      startResendTimer();
    } catch (err: any) {
      setError(err.message || 'Не удалось отправить код');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { token, user } = await authApi.verifyOtp({ email, code });
      onSuccess(token, user);
    } catch (err: any) {
      setError(err.message || 'Неверный код');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
  };

  const canSubmitContact = useMemo(
    () => !!email && (!needsConsent || privacyAccepted),
    [email, needsConsent, privacyAccepted],
  );

  return (
    <>
      {showPrivacySheet && <PrivacySheet onClose={() => setShowPrivacySheet(false)} />}

      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl p-6 pb-8 shadow-xl animate-slide-up">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {step === 'contact' ? (
            <form onSubmit={handleRequestOtp}>
              <div className="text-center mb-6">
                <div className="mx-auto mb-3 flex items-center justify-center">
                  <svg className="w-14 h-14 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    <circle cx="12" cy="16" r="1" fill="currentColor"/>
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Войти в SwipeEat</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Для сохранения истории и избранного
                </p>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                maxLength={255}
                autoFocus
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all"
              />

              {/* Privacy consent — показывается только когда нужно */}
              {needsConsent && (
                <label className="flex items-center gap-2.5 mt-4 cursor-pointer select-none">
                  <div className="relative flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        privacyAccepted
                          ? 'bg-orange-500 border-orange-500'
                          : 'bg-white border-gray-300'
                      }`}
                    >
                      {privacyAccepted && (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="2,6 5,9 10,3" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 leading-relaxed">
                    Я согласен(а) с{' '}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setShowPrivacySheet(true); }}
                      className="text-orange-500 hover:text-orange-600 underline underline-offset-2 transition-colors"
                    >
                      обработкой персональных данных
                    </button>
                  </span>
                </label>
              )}

              <ErrorDisplay error={error} className="mt-3" />

              <button
                type="submit"
                disabled={loading || !canSubmitContact}
                className="w-full mt-4 py-3 rounded-xl bg-orange-500 text-white font-semibold text-sm hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? 'Отправка...' : 'Получить код'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <div className="text-center mb-6">
                <div className="mx-auto mb-3 flex items-center justify-center">
                  <svg className="w-14 h-14 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="M2 7l10 7 10-7"/>
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Введите код</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Код отправлен на <span className="font-medium text-gray-700">{email}</span>
                </p>
              </div>

              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="000000"
                required
                autoFocus
                maxLength={6}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-center text-2xl font-mono tracking-[0.5em] transition-all"
              />

              <ErrorDisplay error={error} className="mt-3" />

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full mt-4 py-3 rounded-xl bg-orange-500 text-white font-semibold text-sm hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? 'Проверка...' : 'Подтвердить'}
              </button>

              {/* Resend */}
              <div className="mt-3 text-center">
                {resendCooldown > 0 ? (
                  <p className="text-xs text-gray-400">
                    Отправить повторно через {resendCooldown} с
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    className="text-xs text-orange-500 hover:text-orange-600 disabled:opacity-50 transition-colors"
                  >
                    Отправить код повторно
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => { setStep('contact'); setCode(''); setError(''); }}
                className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                ← Другой email
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};
